import React from 'react';
import {Box} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import {getValidationLevel, ValidationObjectType} from './builderValidators';
import {VALIDATION} from './BUILDER_VALIDATION';
import {
  BuilderWithdrawalInfoCaptureProps,
  BuilderWithdrawalSummaryValidation,
} from '../../BuilderWithdrawal/BuilderWithdrawalSummaryValidation';
import {NEW_PLAN_ID} from './builderConstants';
import {planFormClasses, PlanFormClassKey} from './planFormClasses';
import {PlanRow} from './PlanRow';
import {PlanSelector} from './PlanSelector';
import {PlanAccordionShell} from './PlanAccordionShell';
import {PlanSubItems} from './PlanSubItems';

import theme from '../../../../theme/Theme';

interface BuilderPlanFormBaseProps {
  id?: string;
  summary: string | JSX.Element;
  disabled?: boolean;
  greyed?: boolean;
  validation?: ValidationObjectType;
  details?: JSX.Element | boolean;
  withdrawalValidation?: BuilderWithdrawalInfoCaptureProps;
  newPlanPotId?: string;
  fixed?: boolean;
  subItems?: JSX.Element | null;
  curoId?: string;
  indent?: boolean;
  isNewWrapperPlanForTransferOrContribution?: boolean;
  reccId: string | null;
  sameReview?: boolean;
  isSelected: boolean;
  isSubplan: boolean;
}

interface SingleChoiceProps extends BuilderPlanFormBaseProps {
  mode?: 'single';
  handleChange: (event: React.ChangeEvent<any> | null, value: string) => void;
}

interface MultipleChoiceProps extends BuilderPlanFormBaseProps {
  mode: 'multiple';
  handleChange: (id: string) => void;
}

export type BuilderPlanFormProps = SingleChoiceProps | MultipleChoiceProps;

export const BuilderPlanForm: React.FC<BuilderPlanFormProps> = (props) => {
  const {
    id = '',
    summary,
    disabled = false,
    greyed = false,
    validation = undefined,
    details,
    withdrawalValidation,
    newPlanPotId = '',
    fixed = false,
    subItems = null,
    curoId,
    indent = false,
    isNewWrapperPlanForTransferOrContribution = false,
    reccId,
    sameReview,
    isSelected,
    isSubplan,
  } = props;

  const mode = props.mode ?? 'single';
  const expanded = Boolean(isSelected && details);

  const validationLevel = getValidationLevel(validation);

  const validationBgClass: PlanFormClassKey =
    validationLevel === VALIDATION.VALID
      ? 'valid'
      : validationLevel === VALIDATION.INVALID
      ? 'invalid'
      : validationLevel === VALIDATION.PARTIALLY_VALID
      ? 'partial'
      : 'nothing';

  const {
    linked = false,
    validatedBank = false,
    needsLink = false,
  } = withdrawalValidation || {};

  const getLinkedBG = (): PlanFormClassKey => {
    if (!needsLink) {
      return 'nothing';
    }
    // eslint-disable-next-line no-nested-ternary
    if (linked) {
      if (validatedBank) {
        return 'valid';
      }
      return 'invalid';
    }
    if (validatedBank) {
      return 'partial';
    }
    return 'invalid';
  };

  const withdrawalBgClass = getLinkedBG();
  const accordionValidationLevel: PlanFormClassKey = needsLink
    ? withdrawalBgClass
    : validationBgClass;

  const selectorHandleChange =
    mode === 'multiple'
      ? () => (props as MultipleChoiceProps).handleChange(id!)
      : () => (props as SingleChoiceProps).handleChange(null, id || NEW_PLAN_ID);

  const summaryTrailing = (
    <>
      <BuilderWithdrawalSummaryValidation
        validated={validatedBank}
        needsLink={needsLink}
        linked={linked}
      />
      {validation && (
        <Box sx={planFormClasses.validationIcon}>
          {validationLevel === VALIDATION.VALID && <CheckIcon />}
          {validationLevel === VALIDATION.PARTIALLY_VALID && <WarningIcon />}
          {validationLevel === VALIDATION.INVALID && <NotInterestedIcon />}
        </Box>
      )}
    </>
  );

  return (
    <PlanRow
      id={id}
      curoId={curoId}
      reccId={reccId}
      isSelected={isSelected}
      isSubplan={indent || isSubplan}
      sameReview={sameReview}
      newPlanPotId={newPlanPotId}
    >
      <PlanAccordionShell
        expanded={expanded}
        disabled={disabled}
        isSelected={isSelected}
        isSoleParent={!isSubplan && !indent}
        validationLevel={accordionValidationLevel}
        greyed={greyed}
        sameReview={sameReview}
        summaryChildren={
          <>
            <PlanSelector
              mode={mode as any}
              id={id}
              disabled={disabled}
              fixed={fixed}
              isSelected={isSelected}
              handleChange={selectorHandleChange}
            />
            <Box
              role="heading"
              flex={1}
              display="flex"
              justifyContent="center"
              flexDirection="column"
            >
              {summary}
            </Box>
          </>
        }
        summaryTrailing={summaryTrailing}
        detailsClassName={isNewWrapperPlanForTransferOrContribution ? '' : 'plan-details'}
      >
        {details || null}
      </PlanAccordionShell>
      <PlanSubItems expanded={expanded}>{subItems}</PlanSubItems>
    </PlanRow>
  );
};

export const BuilderPlanFormsStyled: React.FC<{children: React.ReactNode}> = ({
  children,
}) => (
  <Box
    data-test="Choice"
    sx={{
      width: '100%',
      marginBottom: theme.spacing(1),
    }}
  >
    {children}
  </Box>
);
